import { queryClient } from "./queryClient";
import {
  AWAY_MS,
  assessUpdateSafety,
  type UpdateSafetySignals,
  type UpdateSafetyVerdict,
} from "../../shared/update-safety";

/**
 * 새 버전 갈아타기 — 브라우저에서 "지금 새로고침하면 잃을 게 있나" 신호를 모으는 자리.
 * 판정 규칙은 shared/update-safety.ts(순수 함수·시험). 여기는 DOM·네트워크만 본다.
 *
 * 모으는 신호:
 *  - 마지막 입력 시각(installActivityTracking) — «자리 비움»(입력 없이 30분) 판정에만 쓴다.
 *    마우스를 움직이거나 스크롤만 해도 «자리에 있다» 로 본다 — 읽는 중인 사람을 비운 자리로 오해하지 않게.
 *  - 진행 중인 저장·업로드 요청 수 — window.fetch 를 한 겹 감싸 GET 이 아닌 요청을 센다.
 *    서버 호출이 40여 파일에 흩어져 있어 한 곳에서 세려면 이 길뿐이다. react-query mutation 도 더한다.
 *  - beforeunload 를 막는 화면인가 — 가짜 beforeunload 를 한 번 쏴서 preventDefault 됐는지 본다.
 *    화면마다 따로 등록하지 않아도, "떠나면 잃는다"고 이미 선언한 화면(시세표 편집기·대리점 정보
 *    수정·통화 녹음)이 전부 잡힌다. 앞으로 그런 화면을 만들 때도 beforeunload 만 걸면 된다.
 *  - 떠 있는 창 수 — 공용 Modal·ModalShell 오버레이가 공통으로 다는 `.modal` 클래스와 aria-modal 창을 센다.
 *    머리 줄 「업데이트」 버튼은 창이 아니라 여기 안 걸린다.
 *  - 글이 든 입력칸에 커서가 있나 — 빈 검색창에 커서만 있는 건 잃을 게 없다.
 *  - 보내려고 기다리는 자동 저장 — 화면이 holdPendingSave 로 알린다(입력폼 설정의 1초 자동 저장).
 */

let lastActiveAt = Date.now();
let inFlightWrites = 0;
let pendingSaves = 0;
let installed = false;

/**
 * «곧 저장한다» 를 알린다 — 몇 초 모았다 보내는 자동 저장이, 바꾼 순간부터 저장이 끝날 때까지 쥔다.
 * 쥐고 있는 동안은 화면을 옮겨도·자리를 비워도 새 버전으로 새로고침하지 않는다(보낼 변경이 끊기지 않게).
 * 돌려받은 함수를 부르면 놓는다(여러 번 불러도 한 번만 센다).
 */
export function holdPendingSave(): () => void {
  pendingSaves += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pendingSaves = Math.max(0, pendingSaves - 1);
  };
}

/** 조작·요청 추적을 켠다. 한 번만 설치되며(중복 호출 무시) 해제하지 않는다 — 앱 수명과 같다. */
export function installActivityTracking(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const touch = () => {
    lastActiveAt = Date.now();
  };
  for (const ev of ["pointerdown", "pointermove", "keydown", "input", "wheel", "scroll", "touchstart"] as const) {
    window.addEventListener(ev, touch, { capture: true, passive: true });
  }

  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const method = (
      init?.method ??
      (typeof input !== "string" && !(input instanceof URL) ? input.method : "GET")
    ).toUpperCase();
    const isWrite = method !== "GET" && method !== "HEAD";
    if (isWrite) inFlightWrites += 1;
    try {
      return await original(input, init);
    } finally {
      if (isWrite) inFlightWrites -= 1;
    }
  };
}

/** 마지막 입력(마우스·키보드·스크롤·터치) 시각. */
export function lastActivityAt(): number {
  return lastActiveAt;
}

/** 자리 비움 문턱 — 검증 때는 localStorage 'pb.updateAwayMs' 로 줄여 30분을 기다리지 않는다. */
export function awayThresholdMs(): number {
  try {
    const v = Number(window.localStorage.getItem("pb.updateAwayMs"));
    return v > 0 ? v : AWAY_MS;
  } catch {
    return AWAY_MS;
  }
}

function isUnloadGuarded(): boolean {
  try {
    const probe = new Event("beforeunload", { cancelable: true });
    window.dispatchEvent(probe);
    return probe.defaultPrevented;
  } catch {
    return false;
  }
}

function countOpenModals(): number {
  return document.querySelectorAll('.modal, [aria-modal="true"], dialog[open]').length;
}

/** 커서가 있는 칸에 글이 들어 있나 — 쓰다 만 글이 날아갈 자리. */
function hasTypedText(): boolean {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  if (el instanceof HTMLTextAreaElement) return el.value.trim() !== "";
  if (el instanceof HTMLInputElement) {
    // 체크박스·버튼류·파일은 "쓰다 만 글"이 아니다.
    if (["checkbox", "radio", "button", "submit", "reset", "file", "range", "color"].includes(el.type)) return false;
    return el.value.trim() !== "";
  }
  return el instanceof HTMLElement && el.isContentEditable && (el.textContent ?? "").trim() !== "";
}

export function collectUpdateSafetySignals(
  pathname: string,
  previousPathname?: string | null,
): UpdateSafetySignals {
  return {
    pathname,
    previousPathname,
    unloadGuarded: isUnloadGuarded(),
    openModals: countOpenModals(),
    editing: hasTypedText(),
    inFlightWrites: inFlightWrites + queryClient.isMutating(),
    pendingSaves,
  };
}

/** 지금 새로고침해도 되나. */
export function checkUpdateSafety(pathname: string, previousPathname?: string | null): UpdateSafetyVerdict {
  return assessUpdateSafety(collectUpdateSafetySignals(pathname, previousPathname));
}
